import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    ShieldAlert,
    AlertTriangle,
    Clock,
    Users,
    MapPin,
    Info
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';

interface AddIssuanceRuleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    rule?: any;
}

const AddIssuanceRuleModal: React.FC<AddIssuanceRuleModalProps> = ({ isOpen, onClose, onRefresh, rule }) => {
    const [formData, setFormData] = useState({
        name: '',
        ruleType: 'Value-Based',
        description: '',
        requiresApproval: true,
        isActive: true,
        conditions: {} as any,
        icon: '',
        color: ''
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (rule) {
            setFormData({
                name: rule.name || '',
                ruleType: rule.ruleType || 'Value-Based',
                description: rule.description || '',
                requiresApproval: rule.requiresApproval !== undefined ? rule.requiresApproval : true,
                isActive: rule.isActive !== undefined ? rule.isActive : true,
                conditions: rule.conditions || {},
                icon: rule.icon || '',
                color: rule.color || ''
            });
        } else {
            setFormData({
                name: '',
                ruleType: 'Value-Based',
                description: '',
                requiresApproval: true,
                isActive: true,
                conditions: {},
                icon: '',
                color: ''
            });
        }
    }, [rule, isOpen]);

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Rule name is required');
            return;
        }

        try {
            setLoading(true);

            // Set default icon/color based on type if not editing
            const payload = { ...formData };
            if (!rule) {
                if (payload.ruleType === 'Value-Based') { payload.icon = 'AlertTriangle'; payload.color = 'orange'; }
                else if (payload.ruleType === 'Time-Based') { payload.icon = 'Clock'; payload.color = 'blue'; }
                else if (payload.ruleType === 'Role-Based') { payload.icon = 'Users'; payload.color = 'green'; }
                else if (payload.ruleType === 'Location-Based') { payload.icon = 'MapPin'; payload.color = 'purple'; }
            }

            if (rule) {
                await api.patch(`/api/inventory/issuance-limits/rules/${rule.id}`, payload);
                toast.success('Rule updated successfully');
            } else {
                await api.post('/api/inventory/issuance-limits/rules', payload);
                toast.success('Rule created successfully');
            }
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save rule');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-lg rounded-[32px] p-0 overflow-hidden">
                <div className="p-8 space-y-6 max-h-[95vh] overflow-y-auto custom-scrollbar">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-2xl font-bold">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                <ShieldAlert size={22} className="text-blue-600" />
                            </div>
                            {rule ? 'Edit Rule' : 'Add New Rule'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1">Rule Name</label>
                                <Input
                                    placeholder="e.g., High-Value Items"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-11 font-medium"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1">Rule Type</label>
                                <Select
                                    value={formData.ruleType}
                                    onValueChange={val => setFormData({ ...formData, ruleType: val as any, conditions: {} })}
                                >
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-11 font-medium">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl">
                                        <SelectItem value="Value-Based">Value-Based</SelectItem>
                                        <SelectItem value="Time-Based">Time-Based</SelectItem>
                                        <SelectItem value="Role-Based">Role-Based</SelectItem>
                                        <SelectItem value="Location-Based">Location-Based</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-500 uppercase ml-1">Description</label>
                            <Input
                                placeholder="e.g., Items over $100 require manager approval"
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-11 font-medium"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                            />
                        </div>

                        {/* Conditions based on type */}
                        <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl space-y-4">
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase">
                                <Info size={14} /> Rule Conditions
                            </div>

                            {formData.ruleType === 'Value-Based' && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-medium text-gray-600">Threshold Value ($)</span>
                                        <Input
                                            type="number"
                                            className="w-24 h-9 rounded-lg"
                                            value={formData.conditions.minPrice || ''}
                                            onChange={e => setFormData({ ...formData, conditions: { ...formData.conditions, minPrice: Number(e.target.value) } })}
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 italic">Rule will trigger for items with cost equal to or greater than this value.</p>
                                </div>
                            )}

                            {formData.ruleType === 'Time-Based' && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-medium text-gray-600">Frequency Limit</span>
                                        <Select
                                            value={formData.conditions.frequency || 'monthly'}
                                            onValueChange={val => setFormData({ ...formData, conditions: { ...formData.conditions, frequency: val } })}
                                        >
                                            <SelectTrigger className="w-32 h-9 rounded-lg">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="weekly">Weekly</SelectItem>
                                                <SelectItem value="monthly">Monthly</SelectItem>
                                                <SelectItem value="quarterly">Quarterly</SelectItem>
                                                <SelectItem value="yearly">Yearly</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-medium text-gray-600">Max Quantity</span>
                                        <Input
                                            type="number"
                                            className="w-24 h-9 rounded-lg"
                                            value={formData.conditions.limit || ''}
                                            onChange={e => setFormData({ ...formData, conditions: { ...formData.conditions, limit: Number(e.target.value) } })}
                                        />
                                    </div>
                                </div>
                            )}

                            {formData.ruleType === 'Role-Based' && (
                                <p className="text-xs text-gray-500 italic">Select specific roles this rule applies to in the detailed configuration.</p>
                            )}

                            {formData.ruleType === 'Location-Based' && (
                                <p className="text-xs text-gray-500 italic">Select specific locations this rule applies to in the detailed configuration.</p>
                            )}
                        </div>

                        <div className="flex items-center justify-between p-4 bg-orange-50/30 border border-orange-100/50 rounded-2xl">
                            <div>
                                <p className="text-sm font-bold text-gray-800">Require Approval</p>
                                <p className="text-[10px] text-gray-500">Enable manager approval flow for this rule</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, requiresApproval: !formData.requiresApproval })}
                                className={cn(
                                    "w-10 h-5 rounded-full relative transition-all duration-300",
                                    formData.requiresApproval ? "bg-orange-500" : "bg-gray-200"
                                )}
                            >
                                <div className={cn(
                                    "absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300",
                                    formData.requiresApproval ? "left-6" : "left-1"
                                )} />
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <Button variant="outline" className="flex-1 rounded-2xl h-12 font-bold" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            className="flex-1 rounded-2xl h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold flex items-center justify-center gap-2 shadow-lg"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (rule ? 'Update Rule' : 'Create Rule')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddIssuanceRuleModal;
